/**
 * 认证工具函数
 * 处理JWT Token的存储、获取、刷新等操作
 */

const TOKEN_KEY = 'access_token';
const REFRESH_TOKEN_KEY = 'refresh_token';
const TOKEN_EXPIRES_AT_KEY = 'token_expires_at';
const USER_INFO_KEY = 'user_info';
const PERMISSIONS_KEY = 'user_permissions';
const TOKEN_REFRESH_BUFFER_MS = 5 * 60 * 1000;

let refreshTokenPromise: Promise<TokenInfo | null> | null = null;

export interface TokenInfo {
  access_token: string;
  refresh_token: string;
  expires_in: number;
  expires_at?: number; // 过期时间戳
}

export interface UserInfo {
  id: number;
  username: string;
  name: string;
  email: string;
  avatar: string;
  department: string | null;
  title: string;
  mainAccount: number;
  accountType?: 'education' | 'school' | 'admin' | null;
  accountId?: number | null;
  accountName?: string | null;
  isSubAccount?: boolean;
  access?: string;
  role_id?: number | null;
  roleId?: number | null;
}

export interface UserPermissions {
  menus: string[];
  operations: string[];
}

interface RefreshTokenResponse {
  success?: boolean;
  code?: number;
  message?: string;
  data?: Partial<TokenInfo>;
}

const getApiBaseUrl = (): string => {
  if (typeof window !== 'undefined' && (window as any).APP_CONFIG?.API_BASE_URL) {
    return (window as any).APP_CONFIG.API_BASE_URL;
  }

  if (process.env.REACT_APP_API_BASE_URL) {
    return process.env.REACT_APP_API_BASE_URL;
  }

  return process.env.NODE_ENV === 'development'
    ? 'http://127.0.0.1:8000'
    : '//api.nem.720dt.com';
};

const extractTokenInfo = (responseData: RefreshTokenResponse | null | undefined): TokenInfo | null => {
  const tokenInfo = responseData?.data;

  if (
    tokenInfo?.access_token &&
    tokenInfo.refresh_token &&
    typeof tokenInfo.expires_in === 'number'
  ) {
    return {
      access_token: tokenInfo.access_token,
      refresh_token: tokenInfo.refresh_token,
      expires_in: tokenInfo.expires_in,
    };
  }

  return null;
};

/**
 * 存储token信息
 */
export const setTokenInfo = (tokenInfo: TokenInfo): void => {
  const { access_token, refresh_token, expires_in } = tokenInfo;
  const expires_at = Date.now() + expires_in * 1000;

  localStorage.setItem(TOKEN_KEY, access_token);
  localStorage.setItem(REFRESH_TOKEN_KEY, refresh_token);
  localStorage.setItem(TOKEN_EXPIRES_AT_KEY, expires_at.toString());
};

/**
 * 获取访问token
 */
export const getAccessToken = (): string | null => {
  return localStorage.getItem(TOKEN_KEY);
};

/**
 * 获取刷新token
 */
export const getRefreshToken = (): string | null => {
  return localStorage.getItem(REFRESH_TOKEN_KEY);
};

/**
 * 检查token是否过期
 */
export const isTokenExpired = (): boolean => {
  const expiresAt = localStorage.getItem(TOKEN_EXPIRES_AT_KEY);
  if (!expiresAt) return true;

  return Date.now() >= parseInt(expiresAt, 10);
};

/**
 * 检查token是否即将过期（提前5分钟刷新）
 */
export const isTokenExpiringSoon = (): boolean => {
  const expiresAt = localStorage.getItem(TOKEN_EXPIRES_AT_KEY);
  if (!expiresAt) return true;

  const refreshDeadline = Date.now() + TOKEN_REFRESH_BUFFER_MS;
  return refreshDeadline >= parseInt(expiresAt, 10);
};

/**
 * 刷新访问token，并同步更新本地过期时间
 */
export const refreshAccessToken = async (): Promise<TokenInfo | null> => {
  const refreshToken = getRefreshToken();

  if (!refreshToken) {
    return null;
  }

  if (!refreshTokenPromise) {
    refreshTokenPromise = (async () => {
      const response = await fetch(`${getApiBaseUrl()}/api/refreshToken`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          refresh_token: refreshToken,
        }),
      });

      const responseData = (await response.json()) as RefreshTokenResponse;
      const tokenInfo = extractTokenInfo(responseData);

      if (!response.ok || !tokenInfo || (responseData.success === false && responseData.code !== 200)) {
        throw new Error(responseData?.message || '刷新登录状态失败');
      }

      setTokenInfo(tokenInfo);
      return tokenInfo;
    })().finally(() => {
      refreshTokenPromise = null;
    });
  }

  return refreshTokenPromise;
};

/**
 * 确保请求前拿到有效的访问token
 */
export const ensureValidAccessToken = async (): Promise<string | null> => {
  const token = getAccessToken();

  if (!token) {
    return null;
  }

  if (!isTokenExpiringSoon()) {
    return token;
  }

  const refreshedTokenInfo = await refreshAccessToken();
  return refreshedTokenInfo?.access_token || getAccessToken();
};

/**
 * 初始化时尝试恢复登录态，支持 access token 过期后自动续期
 */
export const tryRestoreSession = async (): Promise<boolean> => {
  if (getAccessToken() && !isTokenExpired()) {
    return true;
  }

  const refreshedTokenInfo = await refreshAccessToken();
  return !!refreshedTokenInfo?.access_token;
};

/**
 * 存储用户信息
 */
export const setUserInfo = (userInfo: UserInfo): void => {
  localStorage.setItem(USER_INFO_KEY, JSON.stringify(userInfo));
};

/**
 * 获取用户信息
 */
export const getUserInfo = (): UserInfo | null => {
  const userInfo = localStorage.getItem(USER_INFO_KEY);
  return userInfo ? JSON.parse(userInfo) : null;
};

/**
 * 存储用户权限
 */
export const setUserPermissions = (permissions: UserPermissions): void => {
  localStorage.setItem(PERMISSIONS_KEY, JSON.stringify(permissions));
};

/**
 * 获取用户权限
 */
export const getUserPermissions = (): UserPermissions | null => {
  const permissions = localStorage.getItem(PERMISSIONS_KEY);
  const result = permissions ? JSON.parse(permissions) : null;
  return result;
};

/**
 * 检查用户是否是超级管理员
 * ⚠️ 注意：此函数仅用于前端UI展示，真正的权限验证在后端
 * 不再在前端判断超级管理员，完全依赖后端返回的权限列表
 */
export const isSuperAdmin = (): boolean => {
  // 不再在前端判断超级管理员
  // 超级管理员的权限由后端返回的权限列表控制
  // 如果后端返回的权限列表中包含所有权限，说明是超级管理员
  // 但真正的判断应该依赖后端返回的数据，前端只做UI展示控制
  return false; // 不在此判断，完全依赖后端返回的权限
};

/**
 * 检查用户是否有某个菜单权限
 * 根据 Ant Design Pro 文档，权限检查应该更加严格
 *
 * ⚠️ 安全说明：
 * 1. 此函数仅用于前端UI显示控制，不能作为安全屏障
 * 2. 超级管理员的权限判断应该由后端返回的权限列表控制
 * 3. 如果后端权限列表中包含该权限，说明用户有权限（包括超级管理员）
 * 4. 后端已经为超级管理员返回所有权限，所以不需要前端再次判断
 */
export const hasMenuPermission = (menuKey: string | string[]): boolean => {
  const permissions = getUserPermissions();

  // 如果没有权限数据，返回 false
  if (!permissions || !permissions.menus || !Array.isArray(permissions.menus)) {
    return false;
  }

  // 如果是数组，检查是否有任一权限
  if (Array.isArray(menuKey)) {
    const hasAnyPermission = menuKey.some(key => permissions.menus.includes(key));
    return hasAnyPermission;
  }

  // 单个权限检查
  const hasPermission = permissions.menus.includes(menuKey);
  return hasPermission;
};

/**
 * 过滤菜单数据，根据用户权限显示菜单
 */
export const filterMenusByPermission = (menuData: any[]): any[] => {
  if (!menuData || menuData.length === 0) {
    return [];
  }

  return menuData.filter(menu => {
    // 如果菜单有子菜单，递归过滤
    if (menu.routes && menu.routes.length > 0) {
      const filteredRoutes = filterMenusByPermission(menu.routes);

      // 如果有选中的子菜单，就显示主菜单和已选中的子菜单
      // 不需要检查父菜单权限，只要有子菜单权限就显示
      if (filteredRoutes.length > 0) {
        return {
          ...menu,
          routes: filteredRoutes
        };
      }
      return false;
    }

    // 如果菜单有权限要求，检查权限（仅对没有子菜单的菜单）
    if (menu.access) {
      if (Array.isArray(menu.access)) {
        return menu.access.some((permission: string) => hasMenuPermission(permission));
      } else {
        return hasMenuPermission(menu.access);
      }
    }

    // 没有权限要求的菜单默认显示
    return true;
  }).map(menu => {
    // 确保子菜单也被正确过滤
    if (menu.routes && menu.routes.length > 0) {
      return {
        ...menu,
        routes: filterMenusByPermission(menu.routes)
      };
    }
    return menu;
  });
};

/**
 * 清理调试相关的本地存储项
 */
export const clearDebugStorage = (): void => {
  // 清理所有可能的调试相关本地存储项
  const debugKeys = [
    'debug_login_permissions',
    'debug_permissions',
    'debug_auth',
    'debug_user_info',
    'debug_token',
  ];

  debugKeys.forEach(key => {
    if (localStorage.getItem(key)) {
      localStorage.removeItem(key);
    }
  });
};

/**
 * 调试权限信息
 */
export const debugPermissions = () => {
  const permissions = getUserPermissions();
  console.log('当前用户权限:', permissions);
  console.log('菜单权限:', permissions?.menus || []);
  console.log('操作权限:', permissions?.operations || []);
};

/**
 * 检查用户是否有某个操作权限
 */
export const hasOperationPermission = (operation: string): boolean => {
  const permissions = getUserPermissions();
  return permissions?.operations.includes(operation) || false;
};

/**
 * 清除所有认证信息
 */
export const clearAuthInfo = (): void => {
  localStorage.removeItem(TOKEN_KEY);
  localStorage.removeItem(REFRESH_TOKEN_KEY);
  localStorage.removeItem(TOKEN_EXPIRES_AT_KEY);
  localStorage.removeItem(USER_INFO_KEY);
  localStorage.removeItem(PERMISSIONS_KEY);
  // 清除调试相关的本地存储项
  localStorage.removeItem('debug_login_permissions');
};

/**
 * 检查是否已登录
 */
export const isAuthenticated = (): boolean => {
  const token = getAccessToken();
  return !!token && !isTokenExpired();
};

/**
 * 从登录响应中保存认证信息
 */
export const saveAuthFromLoginResponse = (loginResult: API.LoginResult): void => {

  if (loginResult.token && loginResult.refresh_token && loginResult.expires_in) {
    // 保存token信息
    setTokenInfo({
      access_token: loginResult.token,
      refresh_token: loginResult.refresh_token,
      expires_in: loginResult.expires_in,
    });

    // 保存用户信息
    if (loginResult.userInfo) {
      // 确保包含必需的字段，包括 account_type
      const userInfoData = loginResult.userInfo as any;
      const userInfo: UserInfo = {
        id: loginResult.userInfo.id,
        username: loginResult.userInfo.username || '',
        name: loginResult.userInfo.name || '',
        email: loginResult.userInfo.email || '',
        avatar: loginResult.userInfo.avatar || '',
        department: loginResult.userInfo.department || null,
        title: loginResult.userInfo.title || '',
        mainAccount: userInfoData.mainAccount ?? 0,
        // 保存 account_type 字段（后端返回的是 account_type，同时保存为 accountType 以兼容）
        accountType: userInfoData.account_type || userInfoData.accountType || null,
        accountId: userInfoData.account_id || userInfoData.accountId || null,
        accountName: userInfoData.account_name || userInfoData.accountName || null,
        isSubAccount: userInfoData.is_sub_account || userInfoData.isSubAccount || false,
        access: userInfoData.access || null,
        role_id: userInfoData.role_id ?? null,
        roleId: userInfoData.roleId ?? userInfoData.role_id ?? null,
      };
      // 同时保存 account_type 字段（下划线命名）以便统一使用
      (userInfo as any).account_type = userInfo.accountType;
      setUserInfo(userInfo);
    } else {
      console.warn('登录响应中没有userInfo字段');
    }

    // 保存用户权限
    if (loginResult.permissions) {
      setUserPermissions(loginResult.permissions);
    } else {
      console.warn('登录响应中没有permissions字段');
    }
  } else {
    console.error('登录响应中缺少必要的token信息');
  }
};

/**
 * 解析JWT Token获取载荷信息（仅用于调试，不用于安全验证）
 */
export const parseJWTPayload = (token: string): any => {
  try {
    const base64Url = token.split('.')[1];
    const base64 = base64Url.replace(/-/g, '+').replace(/_/g, '/');
    const jsonPayload = decodeURIComponent(
      atob(base64)
        .split('')
        .map(c => '%' + ('00' + c.charCodeAt(0).toString(16)).slice(-2))
        .join('')
    );
    return JSON.parse(jsonPayload);
  } catch (error) {
    console.error('Failed to parse JWT payload:', error);
    return null;
  }
};

/**
 * 获取token的剩余有效时间（秒）
 */
export const getTokenRemainingTime = (): number => {
  const expiresAt = localStorage.getItem(TOKEN_EXPIRES_AT_KEY);
  if (!expiresAt) return 0;

  const remaining = parseInt(expiresAt, 10) - Date.now();
  return Math.max(0, Math.floor(remaining / 1000));
};

/**
 * 格式化token剩余时间为可读字符串
 */
export const formatTokenRemainingTime = (): string => {
  const seconds = getTokenRemainingTime();
  if (seconds <= 0) return '已过期';

  const days = Math.floor(seconds / (24 * 60 * 60));
  const hours = Math.floor((seconds % (24 * 60 * 60)) / (60 * 60));
  const minutes = Math.floor((seconds % (60 * 60)) / 60);

  if (days > 0) return `${days}天${hours}小时`;
  if (hours > 0) return `${hours}小时${minutes}分钟`;
  return `${minutes}分钟`;
};
